import { useState, useEffect } from 'react';
import { 
  MessageSquare, Search, RefreshCw, Loader2,
  Clock, AlertCircle, Eye, Trash2, Users,
  ChevronLeft, ChevronRight,
  Bot, User, Pause,
  Zap, TrendingUp,
  Send, Sparkles, FlagOff
} from 'lucide-react';
import { chatService, agentService } from '../../services/PlatformService';
import { useNavigate } from 'react-router-dom';

const ChatsTab = () => {
  const [chats, setChats] = useState([]);
  const [agents, setAgents] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState('last_update_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const navigate = useNavigate();
  const pageSize = 10; // Aumentado para 10 conversas por página

  // Buscar dados dos agentes para cache
  const fetchAgentsData = async (agentIds) => {
    const uniqueIds = [...new Set(agentIds)];
    const newAgents = {};
    
    for (const agentId of uniqueIds) {
      if (!agents[agentId]) {
        try {
          const response = await agentService.getAgentById(agentId);
          if (response?.data) {
            newAgents[agentId] = response.data;
          }
        } catch (err) {
          console.error(`Error fetching agent ${agentId}:`, err);
        }
      }
    }
    
    if (Object.keys(newAgents).length > 0) {
      setAgents(prev => ({ ...prev, ...newAgents }));
    }
  };

  const fetchChats = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const options = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        status: 'active',
        channel: selectedChannel === 'all' ? undefined : selectedChannel
      };
      
      const response = await chatService.getAllChats(options);
      
      let conversationsData = [];
      let totalItems = 0;
      
      if (response && Array.isArray(response.chats)) {
        conversationsData = response.chats;
        totalItems = response.count || conversationsData.length;
      } else if (response && Array.isArray(response)) {
        conversationsData = response;
        totalItems = response.length;
      }
      
      // Filtrar por status (removido "ended")
      if (selectedStatus !== 'all') {
        conversationsData = conversationsData.filter(conv => {
          const state = conv.state || (conv.is_paused ? 'paused' : 'active');
          return selectedStatus === state;
        });
      }
      
      // Filtrar por canal
      if (selectedChannel !== 'all') {
        conversationsData = conversationsData.filter(conv => conv.channel === selectedChannel);
      }
      
      // Filtrar por busca
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        conversationsData = conversationsData.filter(conv => 
          conv.title?.toLowerCase().includes(searchLower) || 
          conv.last_message_preview?.toLowerCase().includes(searchLower) ||
          conv.metadata?.user_name?.toLowerCase().includes(searchLower)
        );
      }
      
      // Ordenar
      conversationsData.sort((a, b) => {
        const valueA = a[sortColumn];
        const valueB = b[sortColumn];
        
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        }
        
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);
        
        return sortDirection === 'asc' 
          ? dateA - dateB 
          : dateB - dateA;
      });
      
      setChats(conversationsData);
      setTotalCount(totalItems);
      
      // Buscar dados dos agentes
      const agentIds = conversationsData.map(chat => chat.agent_id).filter(Boolean);
      if (agentIds.length > 0) {
        await fetchAgentsData(agentIds);
      }
    } catch (err) {
      console.error('Error fetching chats:', err);
      setError('Ops! Não conseguimos carregar as conversas. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChats();
  }, [page, selectedChannel, selectedStatus, searchTerm, sortColumn, sortDirection]);

  const handleViewConversation = (chatId) => {
    navigate(`/agent-chat-detail/${chatId}`);
  };

  const handleDeleteConversation = async (chatId, chatTitle) => {
    if (window.confirm(`Tem certeza que deseja apagar a conversa "${chatTitle || 'sem título'}"? Esta ação não pode ser desfeita.`)) {
      try {
        await chatService.deleteChat(chatId, true);
        setChats(prev => prev.filter(conv => conv.id !== chatId));
        setTotalCount(prev => prev - 1);
        alert('Conversa apagada com sucesso! 🗑️');
      } catch (err) {
        console.error('Error deleting chat:', err);
        alert('Erro ao apagar conversa. Tente novamente.');
      }
    }
  };

  // Função para gerar o ícone do canal
  const getConversationTypeIcon = (channel) => {
    switch (channel) {
      case 'whatsapp':
      case 'whatsapp_business':
        return (
          <img 
            src="https://img.icons8.com/?size=512&id=16713&format=png" 
            alt="WhatsApp" 
            title="WhatsApp" 
            className="w-6 h-6"
          />
        );
      case 'integration':
        return (
          <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
        );
      case 'instagram_direct':
        return (
          <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Instagram_logo_2016.svg/2000px-Instagram_logo_2016.svg.png"} 
            alt="instagram_direct" 
            className="w-5 h-5"
          />
        );
      case 'messenger':
        return (
          <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/b/be/Facebook_Messenger_logo_2020.svg"} 
            alt="messenger" 
            className="w-5 h-5"
          />
        );
      case 'platform':
        return (
          <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-6 h-6 bg-gray-400 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
        );
    }
  };

  // Extrair informações do cliente
  const getClientInfo = (chat) => {
    const metadata = chat.metadata || {};
    let name = metadata.user_name || metadata.client_name || metadata?.custom_field?.nome || metadata.nome || chat.title || 'Cliente Sem Nome';
    const photoUrl = metadata.photo_url || metadata.avatar_url || metadata.foto || null;

    if (metadata?.custom_field?.nome && chat?.title) {
      name = metadata?.custom_field?.nome + " - " + chat?.title
    }

    return { name, photoUrl };
  };

  // Formatar data amigável
  const formatFriendlyDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
      }
      
      if (diffInHours < 24) {
        const hours = Math.floor(diffInHours);
        return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
      }
      
      if (diffInHours < 48) {
        return 'Ontem';
      }
      
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'data inválida';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header com Filtros Avançados */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-green-600" />
            Central de Conversas
          </h3>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-indigo-100 text-green-700 px-4 py-2 rounded-full">
              <TrendingUp className="w-5 h-5" />
              <span className="text-lg font-semibold">
                {totalCount} {totalCount === 1 ? 'Conversa' : 'Conversas'}
              </span>
            </div>
            <button
              onClick={fetchChats}
              className="p-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 transition-all"
              title="Atualizar lista"
            >
              <RefreshCw className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Barra de Busca e Filtros */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
              placeholder="Buscar por cliente, conversa ou mensagem..."
            />
          </div>
          
          <div className="flex gap-3">
            <select
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
              className="px-6 py-4 text-lg font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-2xl hover:bg-gray-50 focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all cursor-pointer"
            >
              <option value="all">Todos os Canais</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="instagram_direct">Instagram Direct</option>
              <option value="messenger">Facebook Messenger</option>
              <option value="platform">Plataforma</option>
              <option value="integration">Integrações</option>
            </select>
            
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-6 py-4 text-lg font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-2xl hover:bg-gray-50 focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all cursor-pointer"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativas</option>
              <option value="paused">Pausadas</option>
              <option value="handoff">Aguardando Atendimento</option>
              <option value="ended">Encerrada</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Conversas Moderna */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
        {isLoading ? (
          <div className="p-20 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-green-100 rounded-3xl mb-4">
              <Loader2 className="h-10 w-10 text-green-600 animate-spin" />
            </div>
            <p className="text-xl text-gray-600">Carregando conversas...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-3xl mb-4">
              <AlertCircle className="h-10 w-10 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{error}</h3>
            <button
              onClick={fetchChats}
              className="mt-4 px-6 py-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 transition-all font-semibold"
            >
              Tentar Novamente
            </button>
          </div>
        ) : chats.length === 0 ? (
          <div className="p-16 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-indigo-100 to-green-100 rounded-full mb-6">
              <MessageSquare className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Nenhuma conversa ainda! 💬
            </h3>
            <p className="text-lg text-gray-600">
              {searchTerm || selectedChannel !== 'all' || selectedStatus !== 'all'
                ? 'Nenhuma conversa encontrada com esses filtros.' 
                : 'Ainda não teve nenhum atendimento.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {chats.map((chat) => {
              const { name: clientName, photoUrl: clientPhotoUrl } = getClientInfo(chat);
              const chatStatus = chat?.state || "active";
              const agent = agents[chat.agent_id];
              
              return (
                <div
                  key={chat.id}
                  className="p-6 hover:bg-indigo-50/30 transition-all cursor-pointer group"
                  onClick={() => handleViewConversation(chat.id)}
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar Section */}
                    <div className="flex -space-x-4 flex-shrink-0">
                      {/* Cliente Avatar */}
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-green-100 border-3 border-white shadow-lg overflow-hidden z-10">
                        {clientPhotoUrl ? (
                          <img 
                            src={clientPhotoUrl} 
                            alt={clientName} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-7 h-7 text-green-600" />
                          </div>
                        )}
                      </div>
                      
                      {/* Agent Avatar */}
                      {agent && (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 border-3 border-white shadow-lg overflow-hidden">
                          {agent.widget_settings?.logo_url ? (
                            <img 
                              src={agent.widget_settings.logo_url} 
                              alt={agent.name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Bot className="w-7 h-7 text-blue-600" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Content Section */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="text-lg font-bold text-gray-900 truncate">
                              {clientName}
                            </h4>
                            
                            {/* Status Badge */}
                            {chatStatus === 'paused' && (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold flex items-center gap-1">
                                <Pause className="w-3 h-3" />
                                Pausada
                              </span>
                            )}
                            {chatStatus === 'ended' && (
                              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center gap-1">
                                <FlagOff className="w-3 h-3" />
                                Encerrada
                              </span>
                            )}
                            {chatStatus === 'handoff' && (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Aguardando Atendimento
                              </span>
                            )}
                            {chatStatus === 'active' && chat.state_metadata?.human_in_loop && (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold flex items-center gap-1">
                                <Send className="w-3 h-3" />
                                Manual
                              </span>
                            )}
                          </div>
                          
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                            {chat.last_message_preview || 'Sem preview de mensagem'}
                          </p>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              {getConversationTypeIcon(chat.channel)}
                              <span className="ml-1">{agent?.name || 'Agente'}</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {chat.message_count || 0} msgs
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatFriendlyDate(chat.last_update_at)}
                            </span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewConversation(chat.id);
                            }}
                            className="p-2 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.05]"
                            title="Ver conversa"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversation(chat.id, chat.title);
                            }}
                            className="p-2 border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-all transform hover:scale-[1.05]"
                            title="Apagar conversa"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Insights Bar (se houver) */}
                      {(chat.message_count > 50 || chatStatus === 'paused') && (
                        <div className="mt-3 flex items-center gap-3">
                          {chat.message_count > 50 && (
                            <div className="flex items-center gap-1 text-xs text-green-600 bg-indigo-50 px-3 py-1 rounded-full">
                              <Sparkles className="w-3 h-3" />
                              Conversa longa
                            </div>
                          )}
                          {chatStatus === 'paused' && chat.state_metadata?.pause_reason && (
                            <div className="text-xs text-gray-600 italic">
                              "{chat.state_metadata.pause_reason}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Paginação Melhorada */}
        {totalCount > pageSize && (
          <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-t-2 border-indigo-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-base text-gray-600">
                Mostrando <span className="font-bold text-green-700">{(page - 1) * pageSize + 1}</span> a{' '}
                <span className="font-bold text-green-700">{Math.min(page * pageSize, totalCount)}</span> de{' '}
                <span className="font-bold text-green-700">{totalCount}</span> conversas
              </p>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-1"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="hidden sm:inline">Anterior</span>
                </button>
                
                {/* Números de página com melhor responsividade */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const totalPages = Math.ceil(totalCount / pageSize);
                    const maxPagesToShow = 5;
                    const maxPagesToShowMobile = 3;
                    const pagesToShow = window.innerWidth < 640 ? maxPagesToShowMobile : maxPagesToShow;
                    
                    let startPage = Math.max(1, page - Math.floor(pagesToShow / 2));
                    let endPage = Math.min(totalPages, startPage + pagesToShow - 1);
                    
                    if (endPage - startPage + 1 < pagesToShow) {
                      startPage = Math.max(1, endPage - pagesToShow + 1);
                    }
                    
                    const pages = [];
                    
                    // Primeira página
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setPage(1)}
                          className="px-3 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-all"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="dots1" className="px-2 text-gray-500">...</span>
                        );
                      }
                    }
                    
                    // Páginas do meio
                    for (let p = startPage; p <= endPage; p++) {
                      pages.push(
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`px-3 py-2 rounded-xl font-semibold transition-all ${
                            page === p 
                              ? 'bg-gradient-to-r from-green-800 to-green-600 text-white shadow-lg' 
                              : 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    }
                    
                    // Última página
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="dots2" className="px-2 text-gray-500">...</span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => setPage(totalPages)}
                          className="px-3 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-all"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <button
                  onClick={() => setPage(prev => Math.min(prev + 1, Math.ceil(totalCount / pageSize)))}
                  disabled={page === Math.ceil(totalCount / pageSize)}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-1"
                >
                  <span className="hidden sm:inline">Próxima</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatsTab;